import { confidenceBandLabel, findingLabel } from './findings';
import type { Prediction } from './schema';
import styles from './review.module.css';

function confidenceClass(band: string): string | undefined {
  if (band === 'medium') {
    return styles.confidenceMedium;
  }
  if (band === 'low') {
    return styles.confidenceLow;
  }
  return styles.confidenceHigh;
}

export interface EvidenceProps {
  readonly noteId: string;
  /** `undefined` for a row the owner added, which has no recognition behind it. */
  readonly prediction: Prediction | undefined;
  readonly fieldLabel: string;
  readonly onShowFullImage: () => void;
}

/**
 * "Show the evidence" (CLAUDE.md): the crop next to the field it came from, the literal reading
 * beside it (never replaced by the interpreted value - rule 2), how sure the model was, and any
 * recognition-time validation flags - with the full photo one tap away rather than shown by
 * default. Those flags are immutable evidence about the prediction, not live errors in the
 * owner's reviewed document. They stay collapsed until explicitly requested, while live
 * arithmetic is rendered separately from the current draft.
 */
export function Evidence({ noteId, prediction, fieldLabel, onShowFullImage }: EvidenceProps) {
  if (prediction === undefined) {
    return (
      <div className={styles.evidence}>
        <p className={styles.evidenceBody}>
          Fila agregada manualmente; no hay evidencia del reconocimiento.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.evidence}>
      {prediction.crop_asset_id !== null ? (
        <img
          className={styles.evidenceCrop}
          src={`/notes/${noteId}/images/${prediction.crop_asset_id}`}
          alt={`Recorte de evidencia: ${fieldLabel}`}
          loading="lazy"
        />
      ) : null}
      <div className={styles.evidenceBody}>
        <span>
          Se leyó:{' '}
          <span className={styles.evidenceLiteral}>
            {prediction.recognized_text === '' ? '(en blanco)' : prediction.recognized_text}
          </span>
        </span>
        {prediction.confidence_band !== null ? (
          <span className={confidenceClass(prediction.confidence_band)}>
            {confidenceBandLabel(prediction.confidence_band)}
          </span>
        ) : null}
        {prediction.validation_flags.length > 0 ? (
          <details className={styles.evidenceOriginalFindings}>
            <summary>
              Incidencias del reconocimiento original ({String(prediction.validation_flags.length)})
            </summary>
            {prediction.validation_flags.map((flag) => (
              <span key={flag} className={styles.evidenceFlag}>
                {findingLabel(flag)}
              </span>
            ))}
          </details>
        ) : null}
        <button type="button" className={styles.linkButton} onClick={onShowFullImage}>
          Ver foto completa
        </button>
      </div>
    </div>
  );
}
